import { inject } from "../../di";
import type { IRpcService } from "../rpc/types";
import type { IAuthState } from "../auth/types";
import type { ISseService } from "../sse/types";
import type { LinkMutatedEvent } from "../sse/sse-events";
import type {
  IWritableLiveEventFormState,
  ILiveEventFormService,
  LiveEventFormData,
  AvailableLink,
  StreamSource,
} from "./types";
import type { GetLiveEventByIdResult } from "api-contracts/rpc/get-live-event-by-id";
import { saveCollaborators, type ExistingCoPromoter } from "../co-promotion-search/save-collaborators";
import { loadCoPromoters } from "../co-promotion-search/load-co-promoters";
import type { CreatorItem } from "../creator-select/types";
import type { CreateLiveEventResult } from "api-contracts/rpc/create-live-event";
import type { UpdateLiveEventResult } from "api-contracts/rpc/update-live-event";
import type { SaveDraftLiveEventResult } from "api-contracts/rpc/save-draft-live-event";
import type { PublishDraftLiveEventResult } from "api-contracts/rpc/publish-draft-live-event";
import { createLiveEventValidators } from "../../form/validators/live-event-validators";
import { NotificationService } from "../../../ui-components/notification";
import { combineDateAndTime, toISODateTime } from "helpers/dateHelpers";

export class LiveEventFormService implements ILiveEventFormService {
  @inject<IWritableLiveEventFormState>("writableLiveEventFormState")
  private state!: IWritableLiveEventFormState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private translateFn: (key: string, params?: Record<string, unknown>) => string = (key: string) => key;
  private pendingLoadEvent: Promise<void> | undefined;
  private pendingLoadEventId: string | undefined;
  private linksLoaded = false;
  private linksLoadSeq = 0;
  private unsubscribeLinksSse: (() => void) | null = null;
  private communitiesLoaded = false;
  private communitiesLoadPromise: Promise<void> | undefined;
  private loadedCoPromoters: ExistingCoPromoter[] = [];

  private get validators() {
    return createLiveEventValidators(this.translateFn);
  }

  setTranslationFunction(translateFn: (key: string, params?: Record<string, unknown>) => string): void {
    this.translateFn = translateFn;
  }

  private clearValidationError(key: string): void {
    const errors = this.state.validationErrors.get();
    if (errors[key]) {
      const rest = { ...errors };
      delete rest[key];
      this.state.validationErrors.set(rest);
    }
  }

  initialize(): void {
    this.loadAvailableLinks();
    this.communitiesLoadPromise = this.loadAvailableCommunities();
  }

  initializeNewEvent(): void {
    if (!this.state.eventId.get() && !this.state.provisionalEventId.get()) {
      this.state.provisionalEventId.set(crypto.randomUUID());
    }
  }

  async loadEvent(id?: string): Promise<void> {
    if (!id) {
      this.state.eventId.set(undefined);
      return;
    }

    if (this.pendingLoadEventId === id && this.pendingLoadEvent) {
      return this.pendingLoadEvent;
    }

    this.pendingLoadEventId = id;
    this.pendingLoadEvent = this.doLoadEvent(id);

    try {
      await this.pendingLoadEvent;
    } finally {
      this.pendingLoadEvent = undefined;
      this.pendingLoadEventId = undefined;
    }
  }

  private async doLoadEvent(id: string): Promise<void> {
    try {
      this.state.isLoading.set(true);
      this.state.eventId.set(id);

      // Refresh attachable-links list now that we know which event we're editing
      this.linksLoaded = false;
      void this.loadAvailableLinks();

      const result = (await this.rpcService.call("get-live-event-by-id", { event_id: id })) as GetLiveEventByIdResult;

      if (!result.event) {
        console.error("Event not found");
        NotificationService.error("Event not found");
        return;
      }

      const event = result.event;

      const scheduledDate = event.scheduled_start_time
        ? (() => {
            const d = new Date(event.scheduled_start_time);
            return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
          })()
        : "";

      const scheduledTime = event.scheduled_start_time
        ? (() => {
            const d = new Date(event.scheduled_start_time);
            return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
          })()
        : "";

      const durationMinutes = event.expected_duration ? event.expected_duration.toString() : "";

      this.state.formData.set({
        title: event.title || "",
        description: event.description || "",
        feeEuros: event.fee_cents ? (event.fee_cents / 100).toFixed(2) : "",
        scheduledDate,
        scheduledTime,
        maxAttendees: event.max_attendees ? event.max_attendees.toString() : "",
        whatsappUrl: event.whatsapp_url || "",
        ctaText: event.cta_text || "",
        durationMinutes,
        language: event.language || "nl",
      });

      // Update UI toggles
      this.state.uiToggles.set({
        showDescription: !!event.description,
        showMaxAttendees: !!event.max_attendees,
        showWhatsApp: !!event.whatsapp_url,
        showCtaText: !!event.cta_text,
        showLinkedLinks: !!(event.linked_links && event.linked_links.length > 0),
        showCoPromotion: false, // loadCoPromoters() will set true if co-promoters exist
        showCommunityOnly: !!event.community_only,
      });

      this.state.communityOnly.set(event.community_only ?? false);
      if (event.community_ids && event.community_ids.length > 0) {
        await this.communitiesLoadPromise;
        this.state.communityIds.set(event.community_ids);
      }

      // Update other state
      this.state.contentTier.set(event.content_tier === "free" ? "free" : "premium");
      this.state.thumbnailFileId.set(event.thumbnail_file_id || undefined);
      this.state.thumbnailUrl.set(event.thumbnail_url || undefined);
      this.state.streamSource.set(event.stream_delivery === "obs" ? "obs" : "webcam");
      this.state.isDraft.set(event.status === "draft");
      this.state.ticketsSold.set(event.tickets_sold ?? 0);
      this.state.originalScheduledStartTime.set(event.scheduled_start_time ?? undefined);

      if (event.linked_links && event.linked_links.length > 0) {
        this.state.linkedLinkAttachments.set(
          event.linked_links.map((link) => ({
            linkId: link.id,
            eventOnly: link.event_only,
          })),
        );
      }

      await this.loadCoPromotersForEvent(id);
    } catch (error) {
      console.error("Error loading event:", error);
      NotificationService.error("Failed to load event");
    } finally {
      this.state.isLoading.set(false);
    }
  }

  private async loadCoPromotersForEvent(eventId: string): Promise<void> {
    try {
      const { loadedCoPromoters } = await loadCoPromoters(this.rpcService, "live_event", eventId, this.state);
      this.loadedCoPromoters = loadedCoPromoters;
    } catch (error) {
      console.error("Error loading co-promoters:", error);
    }
  }

  private async saveCoPromotions(eventId: string): Promise<void> {
    const toggleOn = this.state.uiToggles.get().showCoPromotion;
    const handles = toggleOn ? this.state.collaboratorHandles.get() : [];
    await saveCollaborators(this.rpcService, "live_event", eventId, handles, this.loadedCoPromoters);

    const { loadedCoPromoters: refreshed } = await loadCoPromoters(this.rpcService, "live_event", eventId, this.state);
    this.loadedCoPromoters = refreshed;
  }

  async duplicateEvent(id: string): Promise<void> {
    await this.loadEvent(id);
    // Clear eventId so saveEvent creates a new event instead of updating
    this.state.eventId.set(undefined);
    this.state.isDraft.set(false);
    // Append "(1)" to the title
    const currentData = this.state.formData.get();
    this.state.formData.set({
      ...currentData,
      title: `${currentData.title} (1)`,
    });
  }

  async saveEvent(): Promise<boolean> {
    if (!this.validateForm()) {
      return false;
    }

    const data = this.state.formData.get();
    const eventId = this.state.eventId.get();

    this.state.isSubmitting.set(true);

    try {
      const baseParams = this.buildEventParams(data);

      if (eventId) {
        const updateParams = {
          ...baseParams,
          event_id: eventId,
          fee_cents: data.feeEuros ? Math.round(parseFloat(data.feeEuros) * 100) : null,
          max_attendees: data.maxAttendees ? Number(data.maxAttendees) : null,
          thumbnail_file_id: this.state.thumbnailFileId.get() || null,
          post_buying_video_file_id: null,
          cta_text: data.ctaText?.trim() || null,
          link_attachments: this.state.linkedLinkAttachments.get().map((a) => ({
            link_id: a.linkId,
            event_only: a.eventOnly,
          })),
        };

        const result = (await this.rpcService.call("update-live-event", updateParams)) as UpdateLiveEventResult;
        if (result.success) {
          await this.saveCoPromotions(eventId);
          this.state.originalScheduledStartTime.set(baseParams.scheduled_start_time);
          return true;
        }
      } else {
        const params = {
          ...baseParams,
          event_id: this.state.provisionalEventId.get(),
          post_buying_video_file_id: undefined,
        };

        const result = (await this.rpcService.call("create-live-event", params)) as CreateLiveEventResult;
        if (result.success) {
          const createdEvent = result.event;
          if (createdEvent) {
            await this.saveCoPromotions(createdEvent.id);
            await this.loadEvent(createdEvent.id);
          }
          return true;
        }
      }

      NotificationService.error("Failed to save event");
      return false;
    } catch (error) {
      console.error("Failed to save live event:", error);
      NotificationService.error("Failed to save event. Please try again.");
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async saveAsDraft(): Promise<boolean> {
    const data = this.state.formData.get();
    const eventId = this.state.eventId.get();

    this.state.isSubmitting.set(true);

    try {
      const baseParams = this.buildEventParams(data);

      const params = {
        ...baseParams,
        event_id: eventId ?? this.state.provisionalEventId.get(),
        title: data.title.trim() || undefined,
        scheduled_start_time:
          data.scheduledDate && data.scheduledTime
            ? new Date(`${data.scheduledDate}T${data.scheduledTime}:00`).toISOString()
            : undefined,
      };

      const result = (await this.rpcService.call("save-draft-live-event", params)) as SaveDraftLiveEventResult;
      if (result.success) {
        if (result.event) {
          this.state.eventId.set(result.event.id);
        }
        const savedEventId = this.state.eventId.get();
        if (savedEventId) {
          await this.saveCoPromotions(savedEventId);
        }
        this.state.isDraft.set(true);
        return true;
      }

      NotificationService.error("Failed to save draft");
      return false;
    } catch (error) {
      console.error("Failed to save draft:", error);
      NotificationService.error("Failed to save draft. Please try again.");
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async publishDraft(): Promise<boolean> {
    if (!this.validateForm()) {
      return false;
    }

    const data = this.state.formData.get();
    const eventId = this.state.eventId.get();
    if (!eventId) return false;

    this.state.isSubmitting.set(true);

    try {
      const baseParams = this.buildEventParams(data);

      const params = {
        ...baseParams,
        event_id: eventId,
        title: data.title.trim(),
      };

      const result = (await this.rpcService.call("publish-draft-live-event", params)) as PublishDraftLiveEventResult;
      if (result.success) {
        return true;
      }

      NotificationService.error("Failed to publish event");
      return false;
    } catch (error) {
      console.error("Failed to publish draft:", error);
      NotificationService.error("Failed to publish event. Please try again.");
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  updateFormData(field: keyof LiveEventFormData, value: string): void {
    const currentData = this.state.formData.get();
    this.state.formData.set({
      ...currentData,
      [field]: value,
    });

    this.clearFieldErrorIfValid(field, value);
  }

  validateFieldOnBlur(field: keyof LiveEventFormData): void {
    const data = this.state.formData.get();
    const value = data[field];
    const error = this.validateSingleField(field, value);
    const currentErrors = { ...this.state.validationErrors.get() };

    if (error) {
      currentErrors[field] = error;
    } else {
      delete currentErrors[field];
    }

    if (field === "scheduledDate" || field === "scheduledTime") {
      const timeFieldError = this.validateSingleField("scheduledTime", data.scheduledTime);
      if (timeFieldError) {
        currentErrors.scheduledTime = timeFieldError;
      } else {
        const dateTimeError = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
        if (dateTimeError) {
          currentErrors.scheduledTime = dateTimeError;
        } else {
          delete currentErrors.scheduledTime;
        }
      }
    }

    this.state.validationErrors.set(currentErrors);
  }

  private clearFieldErrorIfValid(field: keyof LiveEventFormData, value: string): void {
    const currentErrors = this.state.validationErrors.get();
    if (!currentErrors[field]) return;

    const error = this.validateSingleField(field, value);
    if (!error) {
      const newErrors = { ...currentErrors };
      delete newErrors[field];

      if ((field === "scheduledDate" || field === "scheduledTime") && newErrors.scheduledTime) {
        const data = this.state.formData.get();
        const updatedData = { ...data, [field]: value };
        const dateTimeError = this.validators.validateScheduledDateTime(
          updatedData.scheduledDate,
          updatedData.scheduledTime,
        );
        if (!dateTimeError) {
          delete newErrors.scheduledTime;
        }
      }

      this.state.validationErrors.set(newErrors);
    }
  }

  private validateSingleField(field: keyof LiveEventFormData, value: string): string | undefined {
    switch (field) {
      case "title":
        return this.validators.validateTitle(value);
      case "description":
        return this.validators.validateDescription(value);
      case "feeEuros":
        return this.validators.createFeeValidator(() => this.state.contentTier.get())(value);
      case "scheduledDate":
        return this.validators.validateDate(value);
      case "scheduledTime":
        return this.validators.validateTime(value);
      case "maxAttendees":
        return value ? this.validators.validateMaxAttendees(value) : undefined;
      case "durationMinutes":
        return this.validators.validateDuration(value);
      default:
        return undefined;
    }
  }

  updateUIToggle(toggle: keyof import("./types").UIToggleState, value: boolean): void {
    const currentToggles = this.state.uiToggles.get();
    this.state.uiToggles.set({
      ...currentToggles,
      [toggle]: value,
    });

    if (toggle === "showCoPromotion" && value === false) {
      this.clearValidationError("collaboratorHandles");
    }
  }

  setThumbnailData(fileId: string | undefined, url?: string): void {
    this.state.thumbnailFileId.set(fileId);
    if (url) {
      this.state.thumbnailUrl.set(url);
    }
    if (fileId) {
      const currentErrors = { ...this.state.validationErrors.get() };
      delete currentErrors.thumbnail;
      this.state.validationErrors.set(currentErrors);
    }
  }

  setCollaboratorHandles(handles: string[], items?: CreatorItem[]): void {
    this.state.collaboratorHandles.set(handles);
    if (items) {
      this.state.existingCollaborators.set(items);
    }
    this.clearValidationError("collaboratorHandles");
    if (handles.length === 0 && this.state.uiToggles.get().showCoPromotion) {
      this.updateUIToggle("showCoPromotion", false);
    }
  }

  toggleLinkedLink(linkId: string, checked: boolean): void {
    const current = this.state.linkedLinkAttachments.get();
    if (checked) {
      if (current.some((a) => a.linkId === linkId)) return;
      this.state.linkedLinkAttachments.set([...current, { linkId, eventOnly: false }]);
    } else {
      this.state.linkedLinkAttachments.set(current.filter((a) => a.linkId !== linkId));
    }
  }

  setLinkedLinkScope(linkId: string, eventOnly: boolean): void {
    const current = this.state.linkedLinkAttachments.get();
    this.state.linkedLinkAttachments.set(current.map((a) => (a.linkId === linkId ? { ...a, eventOnly } : a)));
  }

  validateForm(): boolean {
    const data = this.state.formData.get();
    const contentTier = this.state.contentTier.get();
    const errors: Record<string, string> = {};

    // Title validation
    const titleResult = this.validators.validateTitle(data.title);
    if (titleResult) errors.title = titleResult;

    // Description validation
    const descriptionResult = this.validators.validateDescription(data.description);
    if (descriptionResult) errors.description = descriptionResult;

    // Thumbnail validation
    const thumbnailResult = this.validators.validateThumbnail(this.state.thumbnailFileId.get());
    if (thumbnailResult) errors.thumbnail = thumbnailResult;

    // Fee validation for premium events
    const feeResult = this.validators.createFeeValidator(() => contentTier)(data.feeEuros);
    if (feeResult) errors.feeEuros = feeResult;

    // Date validation
    const dateResult = this.validators.validateDate(data.scheduledDate);
    if (dateResult) errors.scheduledDate = dateResult;

    // Time validation
    const timeResult = this.validators.validateTime(data.scheduledTime);
    if (timeResult) errors.scheduledTime = timeResult;

    // Date+time combined validation (must be in the future)
    if (!errors.scheduledDate && !errors.scheduledTime) {
      const dateTimeResult = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
      if (dateTimeResult) errors.scheduledTime = dateTimeResult;
    }

    // Max attendees validation
    if (data.maxAttendees) {
      const maxAttendeesResult = this.validators.validateMaxAttendees(data.maxAttendees);
      if (maxAttendeesResult) errors.maxAttendees = maxAttendeesResult;
    }

    // Community-only validation
    if (this.state.communityOnly.get() && this.state.communityIds.get().length === 0) {
      errors.communityIds = this.translateFn("createLiveEventPage.communityRequired");
    }

    // Co-promotion validation
    if (this.state.uiToggles.get().showCoPromotion && this.state.collaboratorHandles.get().length === 0) {
      errors.collaboratorHandles = this.translateFn("createLiveEventPage.coPromoterRequired");
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  validateInfoTab(): boolean {
    const data = this.state.formData.get();
    const contentTier = this.state.contentTier.get();
    const errors: Record<string, string> = {};

    // Fee validation for premium events
    const feeResult = this.validators.createFeeValidator(() => contentTier)(data.feeEuros);
    if (feeResult) errors.feeEuros = feeResult;

    // Date validation
    const dateResult = this.validators.validateDate(data.scheduledDate);
    if (dateResult) errors.scheduledDate = dateResult;

    // Time validation
    const timeResult = this.validators.validateTime(data.scheduledTime);
    if (timeResult) errors.scheduledTime = timeResult;

    // Date+time combined validation (must be in the future)
    if (!errors.scheduledDate && !errors.scheduledTime) {
      const dateTimeResult = this.validators.validateScheduledDateTime(data.scheduledDate, data.scheduledTime);
      if (dateTimeResult) errors.scheduledTime = dateTimeResult;
    }

    // Max attendees validation
    if (data.maxAttendees) {
      const maxAttendeesResult = this.validators.validateMaxAttendees(data.maxAttendees);
      if (maxAttendeesResult) errors.maxAttendees = maxAttendeesResult;
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  setContentTier(tier: "free" | "premium"): void {
    this.state.contentTier.set(tier);
    if (tier === "free") {
      const currentData = this.state.formData.get();
      this.state.formData.set({ ...currentData, feeEuros: "" });
      const currentErrors = { ...this.state.validationErrors.get() };
      delete currentErrors.feeEuros;
      this.state.validationErrors.set(currentErrors);
    }
  }

  setStreamSource(source: StreamSource): void {
    this.state.streamSource.set(source);
  }

  setCommunityOnly(value: boolean): void {
    this.state.communityOnly.set(value);
    if (!value) {
      this.state.communityIds.set([]);
    }
  }

  toggleCommunity(communityId: string, checked: boolean): void {
    const current = this.state.communityIds.get();
    if (checked) {
      if (!current.includes(communityId)) {
        this.state.communityIds.set([...current, communityId]);
      }
    } else {
      this.state.communityIds.set(current.filter((id) => id !== communityId));
    }
  }

  shouldConfirmDateTimeChange(): boolean {
    if (!this.state.eventId.get()) return false;
    if (this.state.ticketsSold.get() < 1) return false;

    // Compare at minute precision — the form only captures HH:mm while stored
    // timestamps may carry sub-second precision from fixtures or server clocks.
    const data = this.state.formData.get();
    const current =
      data.scheduledDate && data.scheduledTime
        ? toISODateTime(combineDateAndTime(data.scheduledDate, data.scheduledTime))
        : undefined;
    const original = this.state.originalScheduledStartTime.get()?.slice(0, 16);
    return current !== original;
  }

  reset(): void {
    this.state.reset();
    this.loadedCoPromoters = [];
    this.linksLoaded = false;
    this.communitiesLoaded = false;
    this.communitiesLoadPromise = undefined;
  }

  private async loadAvailableLinks(): Promise<void> {
    if (this.linksLoaded) return;

    // Claim a request sequence number. If doLoadEvent resets linksLoaded and fires
    // a newer call while this one is still in-flight, the newer call bumps seq and
    // the older call's response gets discarded — preventing the stale globals-only
    // response from overwriting the newer globals+scoped response.
    const seq = ++this.linksLoadSeq;

    // Wait for auth to be ready — `initialize()` runs in connectedCallback which
    // fires before the auth cookie/token is wired up. Without this wait the initial
    // RPC lands unauthenticated and fails silently, leaving the picker empty for
    // brand-new events (existing events get saved by the loadEvent refresh, but
    // new events have no follow-up call).
    await this.authState.ready;

    if (seq !== this.linksLoadSeq) return;
    if (this.linksLoaded) return;

    // Always send a UUID as event_id. If there's no saved or provisional id yet (new
    // event before initializeNewEvent has run), synthesise a one-off UUID. The server
    // looks up the event: if not found, it falls back to "globals only" which is the
    // correct behaviour for brand-new events. This keeps the wire shape consistent
    // and sidesteps any strict-schema issues around null/missing fields.
    const savedEventId = this.state.eventId.get();
    const provisionalEventId = this.state.provisionalEventId.get();
    const eventIdForQuery = savedEventId ?? provisionalEventId ?? crypto.randomUUID();

    try {
      this.state.isLoadingLinks.set(true);
      const result = (await this.rpcService.call("get-attachable-links-for-event", {
        event_id: eventIdForQuery,
      })) as { links?: AvailableLink[] };

      if (seq !== this.linksLoadSeq) return;

      if (result?.links) {
        this.state.availableLinks.set(
          result.links.map((link) => ({
            id: link.id,
            title: link.title,
            has_discount: link.has_discount,
            code: link.code,
            event_only: link.event_only,
          })),
        );
      }
      this.linksLoaded = true;
      this.subscribeToLinkMutations();
    } catch (error) {
      if (seq !== this.linksLoadSeq) return;
      console.error("Error loading available links:", error);
      NotificationService.error("Failed to load available links");
    } finally {
      if (seq === this.linksLoadSeq) {
        this.state.isLoadingLinks.set(false);
      }
    }
  }

  private subscribeToLinkMutations(): void {
    if (this.unsubscribeLinksSse) return;

    this.unsubscribeLinksSse = this.sseService.on<LinkMutatedEvent>("link-mutated", () => {
      this.linksLoaded = false;
      void this.loadAvailableLinks();
    });
  }

  private async loadAvailableCommunities(): Promise<void> {
    if (this.communitiesLoaded) return;

    try {
      this.state.isLoadingCommunities.set(true);
      const result = (await this.rpcService.call("get-communities", {})) as {
        communities?: Array<{ id: string; title: string; status: string }>;
      };
      if (result?.communities) {
        const options = result.communities
          .filter((c) => c.status === "published")
          .map((c) => ({ value: c.id, label: c.title }));
        this.state.availableCommunities.set(options);
      }
      this.communitiesLoaded = true;
    } catch (error) {
      console.error("Error loading available communities:", error);
    } finally {
      this.state.isLoadingCommunities.set(false);
    }
  }

  private buildEventParams(data: LiveEventFormData) {
    const streamDelivery = this.state.streamSource.get() === "obs" ? ("obs" as const) : ("browser" as const);
    const scheduledStartTime =
      data.scheduledDate && data.scheduledTime
        ? new Date(`${data.scheduledDate}T${data.scheduledTime}:00`).toISOString()
        : undefined;

    return {
      title: data.title.trim(),
      description: data.description?.trim() || undefined,
      content_tier: this.state.contentTier.get() as "free" | "premium",
      fee_cents: data.feeEuros ? Math.round(parseFloat(data.feeEuros) * 100) : undefined,
      scheduled_start_time: scheduledStartTime,
      max_attendees: data.maxAttendees ? Number(data.maxAttendees) : undefined,
      thumbnail_file_id: this.state.thumbnailFileId.get(),
      whatsapp_url: data.whatsappUrl?.trim() || undefined,
      cta_text: data.ctaText?.trim() || undefined,
      expected_duration: this.calculateDurationMinutes(data.durationMinutes),
      link_attachments: this.state.linkedLinkAttachments.get().map((a) => ({
        link_id: a.linkId,
        event_only: a.eventOnly,
      })),
      stream_delivery: streamDelivery,
      language: data.language || undefined,
      community_only: this.state.communityOnly.get(),
      community_ids: this.state.communityIds.get().length > 0 ? this.state.communityIds.get() : undefined,
    };
  }

  private calculateDurationMinutes(minutes: string): number | undefined {
    if (!minutes) return undefined;
    const minutesNum = Number(minutes);
    return minutesNum > 0 ? minutesNum : undefined;
  }
}
